import React, { useEffect, useState, useRef, useCallback } from "react";
import SockJS from "sockjs-client";
import { Stomp } from "@stomp/stompjs";
import style from "css/queue.module.css";
import { useDispatch } from "react-redux";
import { closeAlert, openAlert } from "../../redux/slices/alertSlice";

const LicenseQueueModal = ({
  isOverlay = true,
  logo,
  userId,
  ticketNumber,
  onSuccess,
  onClose,
}) => {
  const dispatch = useDispatch();
  const rawServerUrl =
    process.env.REACT_APP_SERVER_URL || "http://localhost:8080";
  const serverUrl = rawServerUrl.endsWith("/")
    ? rawServerUrl.slice(0, -1)
    : rawServerUrl;

  const [remaining, setRemaining] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);

  const stompClientRef = useRef(null);
  const maxProcessedRef = useRef(0);
  const checkIntervalRef = useRef(null);
  const wsConnectedRef = useRef(false);

  // 결과 처리 로직 수정
  const handleResult = useCallback(
    (data) => {
      // 타이머 정리
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }

      if (data.status === "SUCCESS") {
        console.log(data);
        onSuccess(data);
      } else if (data.status === "FAIL" || data.status === "SOLD_OUT") {
        // [추가] 실패 메시지를 받았지만, 혹시 DB에는 성공으로 들어갔는지 최종 확인 (Double Check)
        fetch(`${serverUrl}/license/status/${userId}`)
          .then((res) => res.json())
          .then((finalCheck) => {
            const finalData = finalCheck.data || finalCheck;

            // 만약 최종 확인 결과가 SUCCESS라면 성공 처리
            if (finalData.status === "SUCCESS") {
              console.log(
                "Socket reported FAIL but API reported SUCCESS. Recovering...",
              );
              onSuccess(finalData);
              return;
            }

            onClose();
            // 진짜 실패인 경우 알림 표시
            const msg =
              data.status === "SOLD_OUT"
                ? "준비된 수량이 모두 소진되었습니다."
                : data.message || "신청 처리에 실패했습니다.";

            const obj = {
              title: "신청 실패",
              content: <p>{msg}</p>,
              submitEventHandler: () => {
                dispatch(closeAlert());
              },
            };
            dispatch(openAlert(obj));
          })
          .catch(() => {
            onClose();
            // API 호출 실패 시 원래대로 실패 처리
            const obj = {
              title: "신청 실패",
              content: <p>신청 처리에 실패했습니다.</p>,
              submitEventHandler: () => {
                dispatch(closeAlert());
              },
            };
            dispatch(openAlert(obj));
          });
      }
    },
    [dispatch, onClose, onSuccess, serverUrl, userId],
  );

  // 상태 조회 API (폴링)
  const onCheckStatus = useCallback(() => {
    fetch(`${serverUrl}/license/status/${userId}`)
      .then((res) => res.json())
      .then((res) => {
        const data = res.data || res;
        if (["SUCCESS", "FAIL", "SOLD_OUT"].includes(data.status)) {
          handleResult(data);
        }
      })
      .catch((err) => console.error("Status Check Error", err));
  }, [handleResult, serverUrl, userId]);

  // 대기열 표시 업데이트
  const updateQueueDisplay = useCallback(
    (totalProcessedRaw) => {
      const totalProcessed = parseInt(totalProcessedRaw);
      const myTicket = Number(ticketNumber);

      if (totalProcessed < maxProcessedRef.current) return;
      maxProcessedRef.current = totalProcessed;

      const currentRemaining = myTicket - totalProcessed;

      if (currentRemaining <= 0) {
        setIsProcessing(true);
        setProgressPercent(100);
        setRemaining(0);

        // WebSocket 연결 중이면 개인 토픽으로 결과가 오므로 폴링 불필요
        // WebSocket 끊겼을 때만 fallback 폴링
        if (!checkIntervalRef.current && !wsConnectedRef.current) {
          onCheckStatus();
          checkIntervalRef.current = setInterval(onCheckStatus, 3000);
        }
      } else {
        setRemaining(currentRemaining);

        // 퍼센트 계산
        const percent = myTicket > 0 ? (totalProcessed / myTicket) * 100 : 0;
        setProgressPercent(Math.max(5, Math.min(percent, 99)));
      }
    },
    [onCheckStatus, ticketNumber],
  );

  // WebSocket 연결
  const connectSocket = useCallback(() => {
    const socket = new SockJS(serverUrl + "/ws-signup");
    const client = Stomp.over(socket);

    client.debug = () => {};
    stompClientRef.current = client;

    client.connect(
      {},
      () => {
        wsConnectedRef.current = true;
        onCheckStatus();

        // 1. 내 신청 결과 구독
        client.subscribe("/topic/license/status/" + userId, (res) => {
          try {
            handleResult(JSON.parse(res.body));
          } catch (e) {
            console.error("JSON Parse Error", e);
          }
        });

        // 2. 전체 라이선스 처리량 구독 (Backend QueueMonitor와 일치해야 함)
        client.subscribe("/topic/processed", (res) => {
          updateQueueDisplay(res.body);
        });

        // 3. 시스템 리셋 구독
        client.subscribe("/topic/system/reset", () => {
          const obj = {
            title: "알림",
            content: "시스템 점검으로 인해 초기화되었습니다.",
            submitEventHandler: () => {
              dispatch(closeAlert());
              onClose();
            },
          };
          dispatch(openAlert(obj));
        });
      },
      (err) => {
        console.error("Socket Connect Error", err);
        wsConnectedRef.current = false;
        // WebSocket 실패 시 fallback 폴링 시작
        if (!checkIntervalRef.current) {
          checkIntervalRef.current = setInterval(onCheckStatus, 3000);
        }
      },
    );
  }, [
    handleResult,
    onCheckStatus,
    serverUrl,
    updateQueueDisplay,
    userId,
    dispatch,
    onClose,
  ]);

  useEffect(() => {
    connectSocket();

    return () => {
      if (stompClientRef.current) {
        try {
          stompClientRef.current.disconnect();
        } catch (e) {}
      }
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    };
  }, [connectSocket]);

  return (
    <section className={isOverlay ? `${style.overlay}` : ""}>
      <div
        className={
          isOverlay ? `${style.modal} ${style.non_overlay}` : style.modal
        }
      >
        {logo && <section className={`${style.logo_section}`}>{logo}</section>}

        <section className={`${style.counter_section}`}>
          <span className={`${style.counter_label}`}>나의 대기 순서</span>

          <span className={`${style.counter_number}`}>
            {isProcessing ? "0" : remaining !== null ? remaining : "..."}
          </span>

          {/* SignInModal과 동일한 프로그레스바 구조 및 클래스명 */}
          <div className={`${style.counter_progress_bar_bg}`}>
            <div
              className={`${style.progress_bar}`}
              role="progressbar"
              style={{
                width: `${progressPercent}%`,
                transition: "width 0.5s ease-in-out",
              }}
            ></div>
          </div>

          <article className={`${style.counter_subtext}`}>
            <p>현재 신청 인원이 많아 대기 중입니다.</p>
            <p>잠시만 기다려주시면 처리가 완료됩니다.</p>
          </article>
        </section>

        <section className={`${style.description_section}`}>
          <div className={`${style.description_item}`}>
            <div className={`${style.description_title}`}>
              <img src={`${process.env.REACT_APP_CLIENT_URL}img/common/info-icon-black.png`} alt="" />
              <p>안내사항</p>
            </div>
            <div className={`${style.description_content}`}>
              <p>새로고침 하거나 창을 닫으시면</p>
              <p>대기순서가 초기화되어 다시 신청하셔야 합니다.</p>
            </div>
          </div>
          <div className={`${style.contact_item}`}>
            <p>문의</p>
            <div className={`${style.contact_info}`}>
              <img src={`${process.env.REACT_APP_CLIENT_URL}img/common/mail-icon.png`} alt="" />
              <p>이메일: support@example.com</p>
            </div>
            <div className={`${style.contact_info}`}>
              <img src={`${process.env.REACT_APP_CLIENT_URL}img/common/mail-icon.png`} alt="" />
              <p>이메일: contact@example.com</p>
            </div>
          </div>
        </section>
      </div>
    </section>
  );
};

export default LicenseQueueModal;
